import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { 
  Globe, 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  ArrowRight,
  Award,
  Book,
  Users
} from 'lucide-react';

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'Languages',
      links: [
        { name: 'English Courses', href: '#languages' },
        { name: 'French Courses', href: '#languages' },
        { name: 'Spanish Courses', href: '#languages' },
        { name: 'German Courses', href: '#languages' },
        { name: 'Italian Courses', href: '#languages' },
        { name: 'Portuguese Courses', href: '#languages' }
      ]
    },
    {
      title: 'Course Types',
      links: [
        { name: 'Group Classes', href: '#courses' },
        { name: 'Private Tutoring', href: '#courses' },
        { name: 'Online Classes', href: '#courses' },
        { name: 'Intensive Courses', href: '#courses' },
        { name: 'Exam Preparation', href: '#courses' },
        { name: 'Summer Camps', href: '#courses' }
      ]
    },
    {
      title: 'About',
      links: [
        { name: 'Our Story', href: '#about' },
        {/*{ name: 'Our Instructors', href: '#instructors' }, ,
        { name: 'Success Stories', href: '#testimonials' },*/},
        { name: 'Certifications', href: '#about' },
        { name: 'Careers', href: '#contact' },
        { name: 'News & Events', href: '#contact' }
      ]
    },
    {
      title: 'Resources',
      links: [
        { name: 'Free Assessment', href: '#contact' },
        { name: 'Course Schedule', href: '#contact' },
        { name: 'Student Portal', href: '#contact' },
        { name: 'Parent Resources', href: '#contact' },
        { name: 'Language Tips', href: '#contact' },
        { name: 'Cultural Events', href: '#contact' }
      ]
    }
  ];

  const socialLinks = [
    { icon: Facebook, href: '#', name: 'Facebook' },
    { icon: Instagram, href: '#', name: 'Instagram' },
    { icon: Twitter, href: '#', name: 'Twitter' },
    { icon: Youtube, href: '#', name: 'YouTube' }
  ];

  const certifications = [
    'Cambridge English',
    'DELF/DALF',
    'D.E.L.E.',
    'TestDaF',
    'CILS/CELI',
    'CAPLE'
  ];

  return (
    <footer className="bg-gradient-primary text-white">
      {/* Newsletter Section */}
      <div className="border-b border-white/10">
        <div className="container mx-auto px-4 lg:px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h3 className="text-3xl font-bold mb-4">
              Stay Updated with YorkSchool
            </h3>
          </motion.div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="container mx-auto px-4 lg:px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              {/* Logo */}
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-gradient-accent rounded-2xl flex items-center justify-center">
                  <img src={process.env.PUBLIC_URL + '/Final.png'} alt="Logo" className="w-8 h-8" />
                </div>
                <div>
                  <h4 className="text-primary-200 text-xl font-bold">YorkSchool</h4>
                  <p className="text-primary-200 text-sm">Language School</p>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-accent-500 flex-shrink-0" />
                  <span className="text-primary-100">Avenida Minas Gerais,<br /> 6-A 2780-024 OEIRAS</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-accent-500 flex-shrink-0" />
                  <span className="text-primary-100">+351 936 551 878</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-accent-500 flex-shrink-0" />
                  <span className="text-primary-100">yorkschool.oeiras@gmail.com</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-accent-500 flex-shrink-0" />
                  <span className="text-primary-100">{t('openingHours')}</span>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex space-x-4">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={social.name}
                    href={social.href}
                    className="w-10 h-10 bg-white/10 hover:bg-accent-500 rounded-xl flex items-center justify-center transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <social.icon className="w-5 h-5" />
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Footer Links */}
          {footerSections.map((section, sectionIndex) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: sectionIndex * 0.1, duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h5 className="text-lg font-semibold mb-6">{section.title}</h5>
              <ul className="space-y-3">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a
                      href={link.href}
                      className="text-primary-100 hover:text-accent-500 transition-colors duration-300"
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Certifications Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          viewport={{ once: true }}
          className="border-t border-white/10 pt-12 mt-12"
        >
          <h5 className="text-lg font-semibold mb-6 text-center">Official Certification Partners</h5>
          <div className="flex flex-wrap justify-center items-center gap-6">
            {certifications.map((cert, index) => (
              <div
                key={cert}
                className="flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-full"
              >
                <Award className="w-4 h-4 text-accent-500" />
                <span className="text-primary-100 text-sm">{cert}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Bottom Bar */}
      
        <div className="container mx-auto px-4 lg:px-6 py-6">
          <div className="flex justify-center items-center">
            <div className="text-primary-200 text-sm text-center">
              © {currentYear} YorkSchool Language School. All rights reserved.
            </div>
          </div>
      </div>
    </footer>
  );
};

export default Footer;